import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SupportedLanguage, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/config/site.config';
import { TranslationStrings, translations, getTranslation } from '@/config/translations';
import { useReactiveTranslations } from '@/hooks/useReactiveSettings';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: TranslationStrings;
  getLocalizedPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: SupportedLanguage;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  initialLanguage = DEFAULT_LANGUAGE as SupportedLanguage 
}) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(initialLanguage);
  const storedTranslations = useReactiveTranslations();

  useEffect(() => {
    // Update HTML lang attribute
    document.documentElement.lang = language === 'ua' ? 'uk' : language;
  }, [language]);

  const setLanguage = (lang: SupportedLanguage) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  };

  // Используем реактивные переводы
  const t: TranslationStrings = storedTranslations[language];

  const getLocalizedPath = (path: string) => {
    // Remove any existing language prefix
    let cleanPath = path;
    for (const lang of SUPPORTED_LANGUAGES) {
      if (path.startsWith(`/${lang}/`) || path === `/${lang}`) {
        cleanPath = path.slice(lang.length + 1) || '/';
        break;
      }
    }
    
    // For English (default), no prefix
    if (language === 'en') {
      return cleanPath;
    }
    
    // For other languages, add prefix
    if (cleanPath === '/') {
      return `/${language}`;
    }
    return `/${language}${cleanPath}`;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, getLocalizedPath }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Helper to detect language from URL
export const detectLanguageFromPath = (pathname: string): SupportedLanguage => {
  for (const lang of SUPPORTED_LANGUAGES) {
    if (pathname.startsWith(`/${lang}/`) || pathname === `/${lang}`) {
      return lang;
    }
  }
  return DEFAULT_LANGUAGE as SupportedLanguage;
};
